/* src/components/ui/LoadingScreen.js */
import React, { useEffect } from 'react';

// Store loading state in localStorage to persist across navigation
const LOCAL_STORAGE_LOADING_KEY = 'dualNBackLoadingState';

/**
 * A consistent loading screen component for full-page loading states
 * Uses localStorage to maintain loading state across page navigations
 * 
 * @param {Object} props
 * @param {string} props.message - The loading message to display
 * @param {string} props.bgColor - Optional background color (default: 'bg-white')
 * @param {boolean} props.fullScreen - Whether to take up the full viewport
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.persistent - Whether to store loading state in localStorage
 */
const LoadingScreen = ({
  message = "Loading...",
  bgColor = "bg-white",
  fullScreen = true,
  className = "",
  persistent = false
}) => {
  // If persistent is true, store the loading state in localStorage
  useEffect(() => {
    if (persistent) {
      localStorage.setItem(LOCAL_STORAGE_LOADING_KEY, JSON.stringify({
        active: true,
        message,
        timestamp: Date.now()
      }));
      
      // Cleanup function
      return () => {
        localStorage.removeItem(LOCAL_STORAGE_LOADING_KEY);
      };
    }
  }, [persistent, message]);

  // Container classes
  const containerClasses = fullScreen
    ? `fixed inset-0 ${bgColor} flex flex-col items-center justify-center z-50 animate-fadeIn ${className}`
    : `${bgColor} flex flex-col items-center justify-center py-16 animate-fadeIn ${className}`;

  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center justify-center min-h-[200px] px-4 text-center">
        {/* Spinner */}
        <svg
          className="animate-spin h-10 w-10 text-indigo-600 mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        
        {/* Message */}
        {message && (
          <p 
            className="text-lg text-gray-700 font-medium" 
            role="status"
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Static method to check if there's a persistent loading state
 * @returns {Object|null} The loading state or null if none exists
 */
LoadingScreen.getPersistedState = () => {
  try {
    const storedState = localStorage.getItem(LOCAL_STORAGE_LOADING_KEY);
    if (!storedState) return null;
    
    const parsedState = JSON.parse(storedState);
    // Check if the state is still valid (not expired)
    const now = Date.now();
    const expirationTime = 30000; // 30 seconds
    
    if (now - parsedState.timestamp > expirationTime) {
      localStorage.removeItem(LOCAL_STORAGE_LOADING_KEY);
      return null;
    }
    
    return parsedState;
  } catch (e) {
    console.error("Error reading loading state from localStorage:", e);
    return null;
  }
};

export default LoadingScreen;